/**
 * Socket.IO client for real-time updates from the backend.
 * Used to push store_data_updated when admin changes settings (from browser or app).
 */

import { io } from 'socket.io-client';
import apiService from './api';

let socket = null;

function getSocket() {
  if (!socket) {
    const url = apiService.getBaseURL();
    if (!url) return null;
    socket = io(url);
  }
  return socket;
}

/**
 * Subscribe to store data update events. Callback is invoked when admin saves store settings.
 * @param {Function} callback - Called as `callback(payload)` when `store_data_updated` is emitted.
 *   Payload may include `{ data }` (normalized store row from the server). Existing listeners that
 *   ignore arguments remain valid.
 * @returns {Function} Unsubscribe function
 */
export function subscribeToStoreUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('store_data_updated', callback);
  return () => s.off('store_data_updated', callback);
}

/**
 * Subscribe to products update events. Callback is invoked when products are created/updated/deleted.
 * @param {Function} callback - Called when products_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToProductsUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('products_updated', callback);
  return () => s.off('products_updated', callback);
}

/**
 * Subscribe to categories update events. Callback is invoked when categories are created/updated/deleted.
 * @param {Function} callback - Called when categories_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCategoriesUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('categories_updated', callback);
  return () => s.off('categories_updated', callback);
}

/**
 * Subscribe to delivery persons update events. Callback is invoked when delivery persons are created/updated/deleted.
 * @param {Function} callback - Called when delivery_persons_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToDeliveryPersonsUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('delivery_persons_updated', callback);
  return () => s.off('delivery_persons_updated', callback);
}

/**
 * Subscribe to expenses update events. Callback is invoked when expenses or expense categories are created/updated/deleted.
 * @param {Function} callback - Called when expenses_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToExpensesUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('expenses_updated', callback);
  return () => s.off('expenses_updated', callback);
}

/**
 * Subscribe to customers update events. Callback is invoked when customers are created/updated/deleted.
 * @param {Function} callback - Called when customers_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCustomersUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('customers_updated', callback);
  return () => s.off('customers_updated', callback);
}

/**
 * Subscribe to suppliers update events. Callback is invoked when suppliers are created/updated/deleted.
 * @param {Function} callback - Called when suppliers_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToSuppliersUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('suppliers_updated', callback);
  return () => s.off('suppliers_updated', callback);
}

/**
 * Subscribe to users update events. Callback is invoked when users are created/updated/deleted.
 * @param {Function} callback - Called when users_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToUsersUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('users_updated', callback);
  return () => s.off('users_updated', callback);
}

/**
 * Subscribe to sessions update events. Callback is invoked when sessions are opened/closed/force-closed/reviewed.
 * @param {Function} callback - Called when sessions_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToSessionsUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('sessions_updated', callback);
  return () => s.off('sessions_updated', callback);
}

/**
 * Subscribe to purchases update events. Callback is invoked when purchases are created/updated/deleted.
 * @param {Function} callback - Called when purchases_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToPurchasesUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('purchases_updated', callback);
  return () => s.off('purchases_updated', callback);
}

/**
 * Subscribe to orders update events. Callback is invoked when orders are created.
 * @param {Function} callback - Called when orders_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToOrdersUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('orders_updated', callback);
  return () => s.off('orders_updated', callback);
}

/**
 * Subscribe to refunds update events. Callback is invoked when refunds are created.
 * @param {Function} callback - Called when refunds_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToRefundsUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('refunds_updated', callback);
  return () => s.off('refunds_updated', callback);
}

/**
 * Subscribe to offers update events. Callback is invoked when offers change.
 * @param {Function} callback - Called when offers_updated is emitted
 * @returns {Function} Unsubscribe function
 */
export function subscribeToOffersUpdates(callback) {
  const s = getSocket();
  if (!s) return () => {};
  s.on('offers_updated', callback);
  return () => s.off('offers_updated', callback);
}
